import {
  IDEA_FUNCTIONAL_AREAS,
  IDEA_STATUSES,
  type IdeaComment,
  type IdeaFunctionalArea,
  type IdeaStatus,
  type IdeaWithDetails,
} from "@repo/shared/types";
import { enGB, nb } from "date-fns/locale";
import {
  ArrowLeft,
  ChevronUp,
  ImagePlus,
  Languages,
  Loader2,
  MessageSquare,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { del, get, patch, post, postFormData } from "@/lib/api/_client";
import { useSession } from "@/lib/auth-client";
import { useUserLanguage } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { cn, distanceToNowFromDb } from "@/lib/utils";
import type { ActionResult } from "@/pages/ideas/constants";
import { type IdeaSort } from "@/pages/ideas/constants";
import { T } from "./ideas-panel.i18n";

// Badge variants (semantic): success only for done, destructive for rejected,
// everything else neutral gray.
type IdeaBadgeVariant = "secondary" | "success" | "destructive";

const statusVariants: Record<string, IdeaBadgeVariant> = {
  done: "success",
  rejected: "destructive",
};

type View = "list" | "form" | "detail";

export function IdeasPanel() {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const dateLocale = userLanguage === "nb_NO" ? nb : enGB;
  const [isPending, startTransition] = useTransition();
  const { data: session } = useSession();
  const currentUserId = session?.user?.id || null;

  // State
  const [view, setView] = useState<View>("list");
  const [ideas, setIdeas] = useState<IdeaWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isQuantfolio, setIsQuantfolio] = useState(false);

  // Filters
  const [sort, setSort] = useState<IdeaSort>("top");
  const [filterArea, setFilterArea] = useState<IdeaFunctionalArea | "all">("all");

  // Form state
  // Object URL for the not-yet-submitted image: no idea exists to scope a server
  // fetch against until the form is posted.
  const [formImagePreview, setFormImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    functionalArea: "" as IdeaFunctionalArea | "",
    imageUrl: null as string | null,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isResizingImage, setIsResizingImage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Detail view state
  const [selectedIdea, setSelectedIdea] = useState<IdeaWithDetails | null>(null);
  const [comments, setComments] = useState<IdeaComment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Load ideas on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const [ideasData, { isQuantfolio: quantfolioStatus }] = await Promise.all([
          get<IdeaWithDetails[]>("/internal/ideas?sort=top"),
          get<{ isQuantfolio: boolean }>("/internal/ideas/is-quantfolio"),
        ]);
        setIdeas(ideasData);
        setIsQuantfolio(quantfolioStatus);
      } catch (_err) {
        setLoadError(t.loadFailed);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Filter and sort ideas
  const filteredIdeas = ideas
    .filter((idea) => filterArea === "all" || idea.functionalArea === filterArea)
    .sort((a, b) => {
      if (sort === "top") return b.voteCount - a.voteCount;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  // Handlers
  const handleVote = async (idea: IdeaWithDetails, e?: React.MouseEvent) => {
    e?.stopPropagation();
    startTransition(async () => {
      const result = await post<ActionResult<boolean>>(`/internal/ideas/${idea.id}/vote`);
      if (result.success && result.data !== undefined) {
        const newVoteCount = result.data ? idea.voteCount + 1 : idea.voteCount - 1;
        const updatedIdea = { ...idea, hasVoted: result.data, voteCount: newVoteCount };
        setIdeas((prev) => prev.map((i) => (i.id === idea.id ? updatedIdea : i)));
        if (selectedIdea?.id === idea.id) {
          setSelectedIdea(updatedIdea);
        }
      }
    });
  };

  const handleSubmitIdea = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.functionalArea) {
      setFormError(t.selectArea);
      return;
    }

    startTransition(async () => {
      const result = await post<ActionResult<IdeaWithDetails>>("/internal/ideas", {
        title: formData.title,
        description: formData.description,
        functionalArea: formData.functionalArea,
        imageUrl: formData.imageUrl,
      });

      if (!result.success) {
        setFormError(result.error || t.genericError);
        return;
      }

      if (result.data) {
        setIdeas((prev) => [result.data!, ...prev]);
      }

      setFormData({ title: "", description: "", functionalArea: "", imageUrl: null });
      setFormImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setView("list");
    });
  };

  const handleOpenDetail = async (idea: IdeaWithDetails) => {
    setSelectedIdea(idea);
    setView("detail");
    setIsLoadingComments(true);

    try {
      const result = await get<{ idea: IdeaWithDetails | null; comments: IdeaComment[] }>(
        `/internal/ideas/${idea.id}`,
      );
      if (result?.comments) {
        setComments(result.comments);
      }
      if (result?.idea) {
        setSelectedIdea(result.idea);
        setIdeas((prev) => prev.map((i) => (i.id === result.idea!.id ? result.idea! : i)));
      }
    } catch (err) {
      log.error({ err, ideaId: idea.id }, "failed to load idea details");
      setComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedIdea) return;

    startTransition(async () => {
      const result = await post<ActionResult<IdeaComment>>(
        `/internal/ideas/${selectedIdea.id}/comments`,
        { content: newComment.trim() },
      );
      if (result.success && result.data) {
        setComments((prev) => [...prev, result.data!]);
        setSelectedIdea((prev) => (prev ? { ...prev, commentCount: prev.commentCount + 1 } : null));
        setIdeas((prev) =>
          prev.map((i) =>
            i.id === selectedIdea.id ? { ...i, commentCount: i.commentCount + 1 } : i,
          ),
        );
        setNewComment("");
      }
    });
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!selectedIdea) return;

    startTransition(async () => {
      const result = await del<ActionResult>(`/internal/ideas/comments/${commentId}`);
      if (result.success) {
        setComments((prev) => prev.filter((c) => c.id !== commentId));
        setSelectedIdea((prev) => (prev ? { ...prev, commentCount: prev.commentCount - 1 } : null));
        setIdeas((prev) =>
          prev.map((i) =>
            i.id === selectedIdea.id ? { ...i, commentCount: i.commentCount - 1 } : i,
          ),
        );
      }
      setConfirmDeleteCommentId(null);
    });
  };

  const handleDeleteIdea = async () => {
    if (!selectedIdea) return;

    startTransition(async () => {
      const result = await del<ActionResult>(`/internal/ideas/${selectedIdea.id}`);
      if (result.success) {
        setIdeas((prev) => prev.filter((i) => i.id !== selectedIdea.id));
        setView("list");
        setSelectedIdea(null);
      }
      setConfirmDeleteOpen(false);
    });
  };

  const handleStatusChange = async (newStatus: IdeaStatus) => {
    if (!selectedIdea) return;

    startTransition(async () => {
      const result = await patch<ActionResult<IdeaWithDetails>>(
        `/internal/ideas/${selectedIdea.id}`,
        { status: newStatus },
      );
      if (result.success && result.data) {
        setSelectedIdea(result.data);
        setIdeas((prev) => prev.map((i) => (i.id === result.data!.id ? result.data! : i)));
      }
    });
  };

  const canDeleteComment = (createdById: string) => isQuantfolio || createdById === currentUserId;

  // For ideas: non-quantfolio users can only delete if no votes/comments
  const canDeleteIdea = (idea: IdeaWithDetails) => {
    if (isQuantfolio) return true;
    if (idea.createdById !== currentUserId) return false;
    return idea.voteCount === 0 && idea.commentCount === 0;
  };

  // Resize image to max 1024px in any direction and return as Blob
  const resizeImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target?.result as string;
      };

      img.onload = () => {
        const MAX_SIZE = 1024;
        let { width, height } = img;

        // Calculate new dimensions
        if (width > MAX_SIZE || height > MAX_SIZE) {
          if (width > height) {
            height = Math.round((height * MAX_SIZE) / width);
            width = MAX_SIZE;
          } else {
            width = Math.round((width * MAX_SIZE) / height);
            height = MAX_SIZE;
          }
        }

        // Create canvas and resize
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to create blob"));
            }
          },
          "image/jpeg",
          0.85,
        );
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  // Upload image to S3 and return the key
  const uploadImageToS3 = async (file: File): Promise<string> => {
    const resizedBlob = await resizeImage(file);
    const formData = new FormData();
    formData.append("file", resizedBlob, "image.jpg");

    const result = await postFormData<{ s3Key: string }>("/internal/ideas/upload-image", formData);
    return result.s3Key;
  };

  // Get the display URL for an image key
  // Fetched through its IDEA, never by storage key, so the API decides what the
  // caller may see. (The board is cross-tenant by design, so that is every idea.)
  const getIdeaImageUrl = (ideaId: string) =>
    `${process.env.BUN_PUBLIC_API_URL}/internal/ideas/${ideaId}/image`;

  /** Drop the uploaded key and release the object URL behind its preview. */
  const clearFormImage = () => {
    setFormData((prev) => ({ ...prev, imageUrl: null }));
    setFormImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setFormError(t.selectImageFile);
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setFormError(t.imageTooLarge);
      return;
    }

    setIsResizingImage(true);
    setFormError(null);

    try {
      const s3Key = await uploadImageToS3(file);
      setFormData((prev) => ({ ...prev, imageUrl: s3Key }));
      setFormImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    } catch {
      setFormError(t.uploadFailed);
    } finally {
      setIsResizingImage(false);
    }

    // Reset input
    e.target.value = "";
  };

  const handleRemoveImage = () => {
    clearFormImage();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setFormError(t.selectImageFile);
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setFormError(t.imageTooLarge);
      return;
    }

    setIsResizingImage(true);
    setFormError(null);

    try {
      const s3Key = await uploadImageToS3(file);
      setFormData((prev) => ({ ...prev, imageUrl: s3Key }));
      setFormImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    } catch {
      setFormError(t.uploadFailed);
    } finally {
      setIsResizingImage(false);
    }
  };

  // Render based on view
  if (view === "form") {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-6 h-16">
          <Button
            variant="ghost"
            size="icon"
            tooltip={t.back}
            onClick={() => setView("list")}
            className="p-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold">{t.submitIdea}</h2>
        </div>
        <Separator />
        <ScrollArea className="flex-1">
          <form onSubmit={handleSubmitIdea} className="p-6 space-y-4">
            {formError && (
              <div className="text-sm text-destructive bg-destructive/10 p-2 rounded-md">
                {formError}
              </div>
            )}

            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Languages className="h-3.5 w-3.5 shrink-0 mt-px" />
              {t.englishNotice}
            </p>

            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm">
                {t.titleLabel} *
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t.titlePlaceholder}
                maxLength={30}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm">
                {t.descriptionLabel} *
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t.descriptionPlaceholder}
                maxLength={500}
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm">{t.areaLabel} *</Label>
              <Select
                value={formData.functionalArea}
                onValueChange={(v) =>
                  setFormData({ ...formData, functionalArea: v as IdeaFunctionalArea })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t.selectArea} />
                </SelectTrigger>
                <SelectContent>
                  {IDEA_FUNCTIONAL_AREAS.map((area) => (
                    <SelectItem key={area} value={area}>
                      {t[area as keyof typeof t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">{t.imageLabel}</Label>
              {formData.imageUrl ? (
                <div className="relative">
                  <img
                    src={formImagePreview ?? ""}
                    alt={t.screenshotPreview}
                    className="w-full rounded-md border"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={handleRemoveImage}
                  >
                    {t.removeImage}
                  </Button>
                </div>
              ) : (
                <label
                  className={cn(
                    "flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-md cursor-pointer hover:bg-muted/50 transition-colors",
                    isResizingImage && "opacity-50 pointer-events-none",
                    isDragging && "border-primary bg-primary/10",
                  )}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    disabled={isResizingImage}
                  />
                  {isResizingImage ? (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <ImagePlus
                        className={cn(
                          "h-6 w-6",
                          isDragging ? "text-primary" : "text-muted-foreground",
                        )}
                      />
                      <span
                        className={cn(
                          "text-xs mt-1",
                          isDragging ? "text-primary" : "text-muted-foreground",
                        )}
                      >
                        {t.imageHint}
                      </span>
                    </>
                  )}
                </label>
              )}
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setView("list")}
                disabled={isPending}
                className="flex-1"
              >
                {t.cancel}
              </Button>
              <Button type="submit" className="flex-1" loading={isPending}>
                {t.submit}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </div>
    );
  }

  if (view === "detail" && selectedIdea) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-6 h-16">
          <Button
            variant="ghost"
            size="icon"
            tooltip={t.back}
            onClick={() => setView("list")}
            className="p-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold truncate flex-1">{selectedIdea.title}</h2>
          {canDeleteIdea(selectedIdea) && (
            <Button
              variant="ghost"
              size="icon"
              tooltip={t.delete}
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={isPending}
              className="text-destructive p-1"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Separator />
        <ScrollArea className="flex-1">
          <div className="p-6 space-y-4">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">
                {t[selectedIdea.functionalArea as keyof typeof t]}
              </Badge>
              {isQuantfolio ? (
                <Select
                  value={selectedIdea.status}
                  onValueChange={handleStatusChange}
                  disabled={isPending}
                >
                  <SelectTrigger className="h-6 w-auto text-xs px-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IDEA_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {t[status as keyof typeof t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge
                  variant={statusVariants[selectedIdea.status] ?? "secondary"}
                  className="text-xs"
                >
                  {t[selectedIdea.status as keyof typeof t]}
                </Badge>
              )}
            </div>

            {/* Description */}
            <p className="text-sm whitespace-pre-wrap">{selectedIdea.description}</p>

            {/* Image */}
            {selectedIdea.imageUrl && (
              <img
                src={getIdeaImageUrl(selectedIdea.id)}
                alt={t.imageLabel}
                className="w-full rounded-md border"
              />
            )}

            {/* Author & Vote */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Avatar className="h-5 w-5">
                  <AvatarImage src={selectedIdea.createdBy.image || undefined} />
                  <AvatarFallback className="text-[10px]">
                    {selectedIdea.createdBy.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span>{selectedIdea.createdBy.name}</span>
                <span>·</span>
                <span>
                  {distanceToNowFromDb(selectedIdea.createdAt, {
                    addSuffix: true,
                    locale: dateLocale,
                  })}
                </span>
              </div>
              <Button
                variant={selectedIdea.hasVoted ? "default" : "outline"}
                size="sm"
                onClick={() => handleVote(selectedIdea)}
                disabled={isPending}
                className="gap-1"
              >
                <ChevronUp className={cn("h-4 w-4", selectedIdea.hasVoted && "fill-current")} />
                {selectedIdea.voteCount}
              </Button>
            </div>

            <Separator />

            {/* Comments */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                {t.comments} ({comments.length})
              </h3>

              {isLoadingComments ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : comments.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t.noComments}</p>
              ) : (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex gap-2 group">
                      <Avatar className="h-6 w-6 flex-shrink-0">
                        <AvatarImage src={comment.createdBy.image || undefined} />
                        <AvatarFallback className="text-[10px]">
                          {comment.createdBy.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">{comment.createdBy.name}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {distanceToNowFromDb(comment.createdAt, {
                              addSuffix: true,
                              locale: dateLocale,
                            })}
                          </span>
                          {canDeleteComment(comment.createdById) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              tooltip={t.removeImage}
                              className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                              onClick={() => setConfirmDeleteCommentId(comment.id)}
                              disabled={isPending}
                              aria-label={t.delete}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <p className="text-sm mt-0.5">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add comment */}
              <div className="flex gap-2 pt-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t.addComment}
                  rows={2}
                  maxLength={1000}
                  className="flex-1 resize-none text-sm"
                />
                <Button
                  size="icon"
                  tooltip={t.postComment}
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="self-end"
                  loading={isPending}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Languages className="h-3.5 w-3.5 shrink-0 mt-px" />
                {t.englishNotice}
              </p>
            </div>
          </div>
        </ScrollArea>

        {/* Confirm delete idea dialog */}
        <ConfirmDialog
          open={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
          title={t.delete}
          description={t.confirmDelete}
          confirmText={t.delete}
          cancelText={t.cancel}
          onConfirm={handleDeleteIdea}
          variant="destructive"
        />

        {/* Confirm delete comment dialog */}
        <ConfirmDialog
          open={!!confirmDeleteCommentId}
          onOpenChange={(open) => !open && setConfirmDeleteCommentId(null)}
          title={t.delete}
          description={t.confirmDeleteComment}
          confirmText={t.delete}
          cancelText={t.cancel}
          onConfirm={() => confirmDeleteCommentId && handleDeleteComment(confirmDeleteCommentId)}
          variant="destructive"
        />
      </div>
    );
  }

  // List view (default)
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 h-16">
        <h2 className="text-xl font-semibold">{t.title}</h2>
      </div>
      <Separator />

      {/* Filters */}
      <div className="px-6 py-3 flex-shrink-0">
        <div className="flex gap-2">
          <Select value={sort} onValueChange={(v) => setSort(v as IdeaSort)}>
            <SelectTrigger className="h-8 w-20 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="top">{t.sortTop}</SelectItem>
              <SelectItem value="newest">{t.sortNewest}</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filterArea}
            onValueChange={(v) => setFilterArea(v as IdeaFunctionalArea | "all")}
          >
            <SelectTrigger className="h-8 flex-1 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.filterAll}</SelectItem>
              {IDEA_FUNCTIONAL_AREAS.map((area) => (
                <SelectItem key={area} value={area}>
                  {t[area as keyof typeof t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      {/* Ideas List */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : loadError ? (
          <div className="p-6 text-center text-destructive text-sm">{loadError}</div>
        ) : filteredIdeas.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">{t.noIdeas}</div>
        ) : (
          <div className="divide-y">
            {filteredIdeas.map((idea) => (
              // biome-ignore lint/a11y/useSemanticElements: interactive list item with nested buttons
              <div
                key={idea.id}
                className="p-3 hover:bg-muted/50 transition-colors cursor-pointer focus:outline-none focus:bg-muted/50"
                onClick={() => handleOpenDetail(idea)}
                onKeyDown={(e) => e.key === "Enter" && handleOpenDetail(idea)}
                tabIndex={0}
                role="button"
              >
                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "flex flex-col items-center p-1 h-auto min-w-[36px] flex-shrink-0",
                      idea.hasVoted && "text-primary",
                    )}
                    onClick={(e) => handleVote(idea, e)}
                    disabled={isPending}
                  >
                    <ChevronUp className={cn("h-4 w-4", idea.hasVoted && "fill-current")} />
                    <span className="text-xs font-medium">{idea.voteCount}</span>
                  </Button>

                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">{idea.title}</h4>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                      {idea.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-1 mt-1.5">
                      <Badge
                        variant={statusVariants[idea.status] ?? "secondary"}
                        className="text-[10px] px-1.5 py-0"
                      >
                        {t[idea.status as keyof typeof t]}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 ml-1">
                        <MessageSquare className="h-3 w-3" />
                        {idea.commentCount}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Submit button at bottom */}
      <div className="px-6 py-3 flex-shrink-0 border-t">
        <Button size="sm" onClick={() => setView("form")} className="w-full">
          {t.submitIdea}
        </Button>
      </div>
    </div>
  );
}
